"use client";

import { useState, useEffect } from "react";
import styled from "styled-components";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useApp } from "@/contexts/AppContext";
import { doc, getDoc } from "firebase/firestore";
import { db, auth } from "@/lib/firebase";
import { signOut } from "firebase/auth";

const Header = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  transition: all 0.3s ease;

  &.scrolled {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  }
`;

const Nav = styled.nav`
  max-width: 1400px;
  margin: 0 auto;
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;

  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
  }
`;

const LogoButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.3s ease;
  z-index: 1001;

  &:hover {
    opacity: 0.8;
  }

  img {
    height: 40px;
    width: auto;
    object-fit: contain;

    @media (max-width: 768px) {
      height: 32px;
    }
  }
`;

const HamburgerButton = styled.button<{ $isOpen: boolean }>`
  display: none;
  flex-direction: column;
  gap: 4px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  z-index: 1001;

  @media (max-width: 968px) {
    display: flex;
  }

  span {
    width: 24px;
    height: 3px;
    background: #c8553d;
    transition: all 0.3s ease;
    border-radius: 2px;

    &:nth-child(1) {
      transform: ${(props) =>
        props.$isOpen ? "rotate(45deg) translate(5px, 5px)" : "none"};
    }

    &:nth-child(2) {
      opacity: ${(props) => (props.$isOpen ? "0" : "1")};
    }

    &:nth-child(3) {
      transform: ${(props) =>
        props.$isOpen ? "rotate(-45deg) translate(7px, -6px)" : "none"};
    }
  }
`;

const MobileMenu = styled.div<{ $isOpen: boolean }>`
  display: none;

  @media (max-width: 968px) {
    display: flex;
    flex-direction: column;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    padding: 1rem;
    gap: 0.75rem;
    transform: ${(props) =>
      props.$isOpen ? "translateY(0)" : "translateY(-100%)"};
    opacity: ${(props) => (props.$isOpen ? "1" : "0")};
    visibility: ${(props) => (props.$isOpen ? "visible" : "hidden")};
    transition: all 0.3s ease;
    max-height: ${(props) => (props.$isOpen ? "500px" : "0")};
    overflow: hidden;
  }
`;

const MobileNavLink = styled(Link)`
  color: #2c3e50;
  text-decoration: none;
  font-weight: 600;
  font-size: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  transition: all 0.3s ease;

  &:hover {
    background: #f5f5f5;
    color: #c8553d;
  }
`;

const MobileNavButton = styled(Link)<{ $primary?: boolean }>`
  padding: 0.75rem 1rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 8px;
  text-decoration: none;
  text-align: center;
  transition: all 0.3s ease;

  ${(props) =>
    props.$primary
      ? `
    background: #C8553D;
    color: white;
    border: 2px solid #C8553D;

    &:hover {
      background: #a84532;
      border-color: #a84532;
    }
  `
      : `
    background: transparent;
    color: #C8553D;
    border: 2px solid #C8553D;

    &:hover {
      background: #C8553D;
      color: white;
    }
  `}
`;

const MobileLogoutButton = styled.button`
  padding: 0.75rem 1rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 8px;
  transition: all 0.3s ease;
  background: transparent;
  color: #558b8d;
  border: 2px solid #558b8d;
  cursor: pointer;
  text-align: center;

  &:hover {
    background: #558b8d;
    color: white;
  }
`;

const DesktopNavLinks = styled.div`
  display: flex;
  gap: 2rem;
  align-items: center;

  @media (max-width: 968px) {
    display: none;
  }
`;

const DesktopNavLink = styled(Link)`
  color: #2c3e50;
  text-decoration: none;
  font-weight: 600;
  font-size: 1rem;
  transition: color 0.3s ease;

  &:hover {
    color: #c8553d;
  }
`;

const DesktopNavButtons = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;

  @media (max-width: 968px) {
    display: none;
  }

  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

const DesktopNavButton = styled(Link)<{ $primary?: boolean }>`
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 8px;
  text-decoration: none;
  transition: all 0.3s ease;
  white-space: nowrap;

  ${(props) =>
    props.$primary
      ? `
    background: #C8553D;
    color: white;
    border: 2px solid #C8553D;

    &:hover {
      background: #a84532;
      border-color: #a84532;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(200, 85, 61, 0.3);
    }
  `
      : `
    background: transparent;
    color: #C8553D;
    border: 2px solid #C8553D;

    &:hover {
      background: #C8553D;
      color: white;
      transform: translateY(-2px);
    }
  `}

  @media (max-width: 1100px) {
    padding: 0.6rem 1rem;
    font-size: 0.95rem;
  }
`;

const DesktopLogoutButton = styled.button`
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 8px;
  transition: all 0.3s ease;
  background: transparent;
  color: #558b8d;
  border: 2px solid #558b8d;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: #558b8d;
    color: white;
    transform: translateY(-2px);
  }

  @media (max-width: 1100px) {
    padding: 0.6rem 1rem;
    font-size: 0.95rem;
  }
`;

export default function PublicHeader() {
  const { user } = useApp();
  const router = useRouter();
  const [isArtisan, setIsArtisan] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setMobileMenuOpen(false);
      router.push("/");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  useEffect(() => {
    const checkArtisanStatus = async () => {
      if (user?.uid) {
        try {
          const userDoc = await getDoc(doc(db, "accounts", user.uid));
          if (userDoc.exists()) {
            setIsArtisan(userDoc.data().isArtisan || false);
          }
        } catch (error) {
          console.error("Error checking artisan status:", error);
        }
      }
    };

    checkArtisanStatus();
  }, [user]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu when screen size changes to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 968) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <Header className={scrolled ? "scrolled" : ""}>
      <Nav>
        <LogoButton onClick={() => router.push("/")} aria-label="Go to home">
          <Image
            src="/drizl-logo.svg"
            alt="Drizl Logo"
            width={40}
            height={40}
            priority
          />
        </LogoButton>

        {/* Desktop Navigation */}
        <DesktopNavLinks>
          <DesktopNavLink href="/feed">Shop Meals</DesktopNavLink>
          <DesktopNavLink href="/#how-it-works">How It Works</DesktopNavLink>
          <DesktopNavLink href="/#for-artisans">For Artisans</DesktopNavLink>
        </DesktopNavLinks>

        <DesktopNavButtons>
          {user ? (
            <>
              <DesktopNavButton href="/feed">Browse Meals</DesktopNavButton>
              {isArtisan && (
                <DesktopNavButton $primary href="/artisan-dashboard">
                  Dashboard
                </DesktopNavButton>
              )}
              <DesktopLogoutButton onClick={handleLogout}>
                Logout
              </DesktopLogoutButton>
            </>
          ) : (
            <>
              <DesktopNavButton href="/login">Login</DesktopNavButton>
              <DesktopNavButton $primary href="/register">
                Sign Up
              </DesktopNavButton>
            </>
          )}
        </DesktopNavButtons>

        {/* Hamburger Menu Button */}
        <HamburgerButton
          $isOpen={mobileMenuOpen}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </HamburgerButton>

        {/* Mobile Menu */}
        <MobileMenu $isOpen={mobileMenuOpen}>
          <MobileNavLink href="/feed" onClick={closeMobileMenu}>
            Shop Meals
          </MobileNavLink>
          <MobileNavLink href="/#how-it-works" onClick={closeMobileMenu}>
            How It Works
          </MobileNavLink>
          <MobileNavLink href="/#for-artisans" onClick={closeMobileMenu}>
            For Artisans
          </MobileNavLink>

          {user ? (
            <>
              <MobileNavButton href="/feed" onClick={closeMobileMenu}>
                Browse Meals
              </MobileNavButton>
              {isArtisan && (
                <MobileNavButton
                  $primary
                  href="/artisan-dashboard"
                  onClick={closeMobileMenu}
                >
                  Dashboard
                </MobileNavButton>
              )}
              <MobileLogoutButton onClick={handleLogout}>
                Logout
              </MobileLogoutButton>
            </>
          ) : (
            <>
              <MobileNavButton href="/login" onClick={closeMobileMenu}>
                Login
              </MobileNavButton>
              <MobileNavButton
                $primary
                href="/register"
                onClick={closeMobileMenu}
              >
                Sign Up
              </MobileNavButton>
            </>
          )}
        </MobileMenu>
      </Nav>
    </Header>
  );
}
